import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Scissors } from 'lucide-react';
import SplitSeasonReportControls from './SplitSeasonReportControls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_STYLE = {
  mated_laying: 'bg-lime-500', failed: 'bg-rose-500', swarmed: 'bg-amber-500', combined: 'bg-sky-500',
};

const RateBar = ({ row, outcomes }) => (
  <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-stone-100">
    {Object.keys(outcomes).map(k => row[k] > 0 && (
      <div key={k} className={OUTCOME_STYLE[k]} style={{ width: `${(row[k] / row.recorded) * 100}%` }} title={`${outcomes[k]}: ${row[k]}`} />
    ))}
  </div>
);

export const SplitResultsCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/hive-splits/outcome-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data)).catch(() => setData({ methods: [], total: 0 }));
  }, []);

  if (!data) return null;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm" data-testid="split-results-card">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <h2 className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
          <Scissors className="h-6 w-6 text-amber-600" /> Split Results
        </h2>
        {data.success_rate !== null && data.success_rate !== undefined && (
          <div className="text-sm text-stone-600" data-testid="split-results-overall">
            Overall success (all time) <span className={`text-2xl font-extrabold ml-1 ${data.success_rate >= 60 ? 'text-lime-700' : data.success_rate >= 30 ? 'text-amber-700' : 'text-rose-700'}`}>{data.success_rate}%</span>
            <span className="text-stone-400 ml-1">({data.recorded} of {data.total} recorded)</span>
          </div>
        )}
      </div>
      <div className="mb-4"><SplitSeasonReportControls /></div>
      {data.methods.length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="split-results-empty">No splits yet. Record how each split turned out on the hive page to see success rates here.</p>
      ) : (
        <div className="space-y-4">
          {data.methods.map(row => (
            <div key={row.method} data-testid={`split-results-row-${row.method}`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="font-semibold text-stone-800">{row.method}</span>
                <span className="text-sm text-stone-600">
                  {row.success_rate === null ? 'No results recorded' : <><strong className={row.success_rate >= 60 ? 'text-lime-700' : row.success_rate >= 30 ? 'text-amber-700' : 'text-rose-700'}>{row.success_rate}%</strong> success</>}
                  <span className="text-stone-400"> · {row.recorded}/{row.total} recorded</span>
                </span>
              </div>
              {row.recorded > 0 && <RateBar row={row} outcomes={data.outcomes} />}
            </div>
          ))}
          <div className="flex flex-wrap gap-3 pt-2 text-xs text-stone-600">
            {Object.entries(data.outcomes).map(([k, label]) => (
              <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${OUTCOME_STYLE[k]}`} />{label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SplitResultsCard;
